import React from "react";
import Modal from "../../../../components/common/Modal";
import ContentToggle from "../../../../components/common/ContentToggle";

interface TestOfChangeHelpModalProps {
  /** Controls whether the modal is visible */
  isOpen: boolean;
  /** Callback fired when the user closes the modal */
  onClose: () => void;
}

const MODAL_STYLES = `
  #modal-manual-trig .modal-container {
    max-width: 100% !important;
    width: 100% !important;
    top: 10% !important;
  }
`;

/**
 * TestOfChangeHelpModal - Modal help dialog for the Test of Change page.
 *
 * Displays frequently asked questions about the Test of Change tab using
 * expandable ContentToggle sections. Opened from the TOC help popup when
 * the user clicks "additional page help".
 *
 * @param isOpen - Whether the modal is currently visible.
 * @param onClose - Callback to close the modal.
 * @returns The TOC help modal component.
 */
const TestOfChangeHelpModal: React.FC<TestOfChangeHelpModalProps> = ({
  isOpen,
  onClose,
}) => (
  <Modal
    isOpen={isOpen}
    title="TOC Questions & Answers"
    onClose={onClose}
    onSecondary={onClose}
    clearBtn="Close"
    contentClassName="mb-4!"
  >
    <div className="py-2">
      <ContentToggle titleText="What's new in Test of Change?" isFillOption>
        <p className="text-[14px]!">
          UBT Tracker has help icons located on the right upper side of selected
          pages and by most fields. These help icons offer instructions on how
          to fill out the field, view a project data sample or a LMP website
          link.
        </p>
      </ContentToggle>

      <ContentToggle titleText="Entering Test of Change Details" isFillOption>
        <p className="text-[14px]! mb-2!">
          After selecting a Project and defining a SMART goal, you are ready to
          label and collect your team&rsquo;s small test of changes. The Test of
          Change (TOC) Tab allows you to date, name, and record the outcome of
          your team&rsquo;s efforts.
        </p>
        <img src="/assets/images/toc-details.png" alt="UBT_TOCCompleteEditForm" width="946" height="646" />
        <img src="/assets/images/toc-add-new-test.png" alt="UBT_TOCCompleteEditForm" width="946" height="646" />
        <p className="mt-3! text-[14px]!">
          Your team may record multiple attempts to improve a process by adding
          different TOC records. Each attempt to improve a process is recorded
          as a new TOC. Each result for the attempts to improve a process is
          recorded in the{" "}
          <span className="underline">Test Data Tab</span>.
        </p>
      </ContentToggle>

      <ContentToggle
        titleText="How can I enter my team's baseline and target through the Test of Change Details Tab?"
        isFillOption
      >
        <p className="text-[14px]!">
          You may establish your baseline and/or target by creating a Test of
          Change (TOC) for each. For example, add a TOC called
          &ldquo;Target&rdquo;. Next, add 2 data points on the Test Data Tab.
          Enter the test data information (i.e. start date, target value, end
          date):{" "}
          <span className="underline!">
            Note: the test data should have the start date as the first day of
            the project and the end date as the last day of the project.
          </span>{" "}
          Your graph results will display the test data from other TOCs compared
          to your &ldquo;Target&rdquo; line (i.e. Single line from the
          beginning to the end of the project).
        </p>
      </ContentToggle>

      <ContentToggle titleText="How do I sort my results?" isFillOption>
        <p className="text-[14px]!">
          Click on the column header to sort your TOC records.
        </p>
        <p className="mt-3! text-[14px]!">
          See Also:{" "}
          <a
            href="https://www.lmpartnership.org/tools/ubt-tracker-user-guide"
            target="_blank"
            rel="noopener noreferrer"
            className="text-kp-blue-dark! underline! hover:text-kp-blue-dark!"
          >
            https://www.lmpartnership.org/tools/ubt-tracker-user-guide
          </a>
        </p>
      </ContentToggle>
    </div>
    <style>{MODAL_STYLES}</style>
  </Modal>
);

export default TestOfChangeHelpModal;
